import React from 'react';
import { render } from 'ink';
import { Command } from 'commander';
import { ConfigService } from '../services/config.service.js';
import { ComponentsService } from '../services/components.service.js';
import { ValidationError } from '../errors/index.js';
import { UnifiedApplyApp } from '../ui/apply/UnifiedApplyApp.js';
import { ErrorLogService } from '../services/error-log.service.js';
import { getCliName } from '../utils/cli-name.js';
import { runSync } from './ide/sync.js';
import type { MigrateIDE } from '../types/migrate-ide.types.js';
import { assertPrimaryPackageCompatibility } from '../services/primary-package-policy.service.js';

/** Map user-friendly IDE names to internal MigrateIDE ids */
const TARGET_IDE_MAP: Record<string, MigrateIDE> = {
    'cursor': 'cursor',
    'windsurf': 'windsurf',
    'antigravity': 'antigravity',
    'claude': 'claude',
    'claudecode': 'claude',
    'codex': 'codex',
    'codexcli': 'codex',
    'openai-codex': 'codex',
    'opencode': 'opencode',
};

/**
 * Create apply command with Unified Ink UI
 */
export function createApplyCommand(): Command {
    const cmd = new Command('apply')
        .description('Apply components to your project (interactive UI)')
        .argument('[items...]', 'Package name or component filepaths (non-interactive mode)')
        .option('--force', 'Force overwrite even if modified locally')
        .option('--no-interactive', 'Disable interactive UI (for CI/CD)')
        .option('--target <ides...>', 'Auto-sync to target IDE(s) after apply (claude, cursor, windsurf, etc.)')
        .action(async (items: string[], options: { force?: boolean; interactive?: boolean; target?: string[] }) => {
            const configService = new ConfigService();
            const config = await configService.load();
            if (!config) {
                throw new ValidationError(`Not initialized. Run "${getCliName()} auth" first.`);
            }

            // Non-interactive mode or direct arguments
            if (options.interactive === false || items.length > 0) {
                await nonInteractiveApply(config, items, options);
                return;
            }

            // Interactive mode with Unified Ink UI
            const { waitUntilExit } = render(
                React.createElement(UnifiedApplyApp, {
                    config: config,
                    force: options.force,
                    onExit: () => {
                        process.exit(0);
                    }
                })
            );

            await waitUntilExit();
        });

    return cmd;
}

/**
 * Non-interactive apply (for CLI args or CI/CD)
 */
async function nonInteractiveApply(
    config: any,
    items: string[],
    options: { force?: boolean; target?: string[] }
) {
    const componentsService = new ComponentsService();
    const name = getCliName();

    if (items.length === 0) {
        console.log('❌ No items specified. Use interactive mode or provide package/component names.');
        console.log(`💡 Usage: ${name} apply <package> or ${name} apply <filepath...>`);
        return;
    }

    // Check if first arg is a package
    const tags = await componentsService.listTags(config);
    const packageNames = tags.map(t => t.tag);

    let filepaths: string[];

    if (items.length === 1 && packageNames.includes(items[0]!)) {
        // Package mode
        console.log(`📦 Applying package '${items[0]}'...`);
        const components = await componentsService.list(config, { tag: items[0] });
        filepaths = components.map(c => c.filepath);

        // Package has fixed target → use it, ignore --target flag
        const matchedTag = tags.find(t => t.tag === items[0]);
        if (matchedTag?.defaultTarget?.length) {
            if (options.target) {
                console.log(`⚠️  Package '${items[0]}' has fixed target: ${matchedTag.defaultTarget.join(', ')}. Ignoring --target.`);
            }
            options.target = matchedTag.defaultTarget;
            console.log(`🎯 Target: ${options.target.join(', ')}`);
        }
    } else {
        // Direct filepaths
        filepaths = items;
    }

    // Resolve dependencies
    console.log('🔍 Resolving dependencies...');
    const resolvedPaths = await componentsService.resolveWithDependencies(config, filepaths);

    // Guard before checksums, downloads, or any filesystem write.
    await assertPrimaryPackageCompatibility({
        projectRoot: process.cwd(),
        requestedPackages: items,
        requestedPaths: resolvedPaths,
        packageMetadata: tags,
    });

    console.log(`📦 Will apply ${resolvedPaths.length} component(s):`);
    for (const fp of resolvedPaths) {
        const isDep = !filepaths.includes(fp);
        const suffix = isDep ? ' (dependency)' : '';
        console.log(`   - ${fp}${suffix}`);
    }

    // Install
    const targetDir = process.cwd() + '/.jai1';
    const installed = await componentsService.getInstalled();
    let added = 0;
    let updated = 0;
    let skipped = 0;
    const failures: Array<{ filepath: string; error: string }> = [];

    // Fetch server checksums for change detection
    const serverChecksums = await componentsService.getChecksums(config, resolvedPaths);

    for (const fp of resolvedPaths) {
        try {
            const info = installed[fp];

            if (info && !options.force && info.modified) {
                console.log(`⚠️  ${fp}: modified locally. Use --force to overwrite.`);
                skipped++;
                continue;
            }

            // Skip if already up to date (checksum match)
            if (info && !options.force) {
                const serverInfo = serverChecksums[fp];
                if (serverInfo && info.checksum === serverInfo.checksum) {
                    skipped++;
                    continue;
                }
            }

            console.log(`📥 Downloading ${fp}...`);
            await componentsService.install(config, fp, targetDir);

            if (info) updated++;
            else added++;

            console.log(`✅ ${fp}`);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Error';
            console.log(`❌ ${fp}: ${message}`);
            failures.push({ filepath: fp, error: message });
        }
    }

    if (failures.length > 0) {
        void new ErrorLogService().logError(new Error('apply failed'), {
            command: 'apply',
            cwdBase: process.cwd().split('/').filter(Boolean).pop(),
            details: { failures },
        });
    }

    console.log(`\n✅ Complete: ${added} added, ${updated} updated, ${skipped} up-to-date`);
    console.log(`📁 Location: ${targetDir}`);

    // Auto-sync to target IDEs if --target specified
    if (options.target && options.target.length > 0) {
        const resolvedIdes = options.target
            .map(ide => TARGET_IDE_MAP[ide.toLowerCase()])
            .filter((ide): ide is MigrateIDE => !!ide);

        if (resolvedIdes.length > 0) {
            console.log(`\n🔄 Auto-syncing to IDE(s): ${resolvedIdes.join(', ')}...`);
            await runSync({ ides: resolvedIdes, yes: true });
        } else {
            const valid = Object.keys(TARGET_IDE_MAP).join(', ');
            console.log(`\n⚠️  Unknown --target IDE(s): ${options.target.join(', ')}. Valid: ${valid}`);
        }
    } else {
        console.log(`\n💡 Bước tiếp theo:`);
        console.log(`   • Chạy "${name} ide sync" để đồng bộ nội dung sang các IDE bạn đang dùng.`);
        console.log(`   • Chạy "${name} doctor" để kiểm tra dự án đã setup Jai1 đầy đủ chưa.`);
    }
}
